/*
 * Copyright (c) 2024 Amazon.com, Inc. All Rights Reserved.
 */
import {Navbar, NavbarPages} from "../navbar/Navbar";
import {StageConfig} from "../../config/StageConfig";
import {useEffect, useRef, useState} from "react";
import {useOutsideClick} from "../../hooks/useOutsideClick";
import {Snackbar} from "@mui/material";
import {AntiSignatureForm} from "./AntiSignatureForm";
import {RequestType} from "../../model/RequestType";
import {AntiSignatureTable} from "./AntiSignatureTable";
import {AntiSignatureEntry} from "../../model/AntiSignatureEntry";
import sentryFetch from "@amzn/sentry-fetch";
import {isJSONResponse} from "../../config/Requests";
import "../models/ModelPage.css";
/**
 * @author pdoriand Sep 04, 2024
 */
export function AntiSignaturePage() {
    const PAGE_SIZES = [10, 20, 30, 40, 50];
    const [antiSignatures, setAntiSignatures] = useState<AntiSignatureEntry[]>([]);
    const [stageConfig, setStageConfig] = useState<StageConfig>();
    const [showMessage, setShowMessage] = useState<boolean>(false);
    const [responseMessage, setResponseMessage] = useState<string>("");
    const [page, setPage] = useState<number>(1);
    const [valuesPerPage, setValuesPerPage] = useState<number>(10);
    const [showCreate, setShowCreate] = useState<boolean>(false);

    useEffect(() => {
        if (showCreate) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }
        return () => document.body.classList.remove('modal-open');
    }, [showCreate]);

    const createButtonRef = useRef(null);
    const createAntiSignatureRef = useRef(null);

    useEffect(() => {
        fetch("../stageConfig.json")
            .then(res => res.json())
            .then(stageConfig => setStageConfig(stageConfig));
    }, []);

    // These hooks are used for filtering the antisignatures
    const [region, setRegion] = useState("");
    const [description, setDescription] = useState("");
    const [ttl, setTtl] = useState<string>("");
    const [antiSignatureModels, setAntiSignatureModels] = useState("");
    const [antiSignatureLabels, setAntiSignatureLabels] = useState("");
    const [signatureType, setSignatureType] = useState("");
    const [signatureFeatures, setSignatureFeatures] = useState(new Map<string, string>());

    const refreshAntiSignatures = () => {
        if (!stageConfig) return;

        sentryFetch(`${stageConfig.url}/modelsManager?requestType=${RequestType.SEARCH_ANTISIGNATURES}` +
            `&region=${region || ""}&description=${description || ""}&ttl=${ttl || ""}` +
            `&models=[${antiSignatureModels}]&labels=[${antiSignatureLabels}]&signatureType=${signatureType || ""}` +
            `&aggregatedBy=${encodeURIComponent(JSON.stringify(Object.fromEntries(signatureFeatures)))}&page=${page}&valuesPerPage=${valuesPerPage}`, {
            method: "GET",
            credentials: "include",
            sentryOptions: {
                followMidwayStepUp: true
            }
        }).then((response) => {
            if (response.ok) {
                return response.json();
            } else {
                return response.text().then(response => {
                    if (isJSONResponse(response)) {
                        throw new Error(JSON.parse(response).message);
                    } else {
                        throw new Error(response);
                    }
                });
            }
        })
            .then(antisignatures => {
                setAntiSignatures(antisignatures);
            })
            .catch(error => {
                console.error(error.message);
                setResponseMessage(error.message);
                setShowMessage(true);
            })
    }
    useEffect(() => {
        refreshAntiSignatures();
    }, [stageConfig, page, valuesPerPage, region, description, ttl, antiSignatureModels, antiSignatureLabels, signatureType, signatureFeatures]); // eslint-disable-line react-hooks/exhaustive-deps
    useOutsideClick([createButtonRef, createAntiSignatureRef], () => setShowCreate(false));
    return <>
        {stageConfig && <div>
            <Navbar selectedPage={NavbarPages.Antisignatures} stage={stageConfig.stage}/>
            <div className="table-container">
                <div className="buttons">
                    <button className="add-button" ref={createButtonRef} onClick={() => setShowCreate(true)}>+</button>
                    <div className="page-buttons">
                        <label htmlFor="perPage">Page size:</label>
                        <select id="perPage" defaultValue={10} onChange={e => {
                                setValuesPerPage(parseInt(e.target.value))
                                setPage(1);
                            }
                        }>
                            {PAGE_SIZES.map((pageSize, idx) =>
                                <option value={pageSize} key={idx}>{pageSize}</option>
                            )}
                        </select>
                        <button onClick={() => setPage(page - 1)} disabled={page === 1}>{"<"}</button>
                        <span>Page {page}</span>
                        <button disabled={antiSignatures && antiSignatures.length < valuesPerPage} onClick={() => setPage(page + 1)}>{">"}</button>
                    </div>
                </div>
                {showCreate && <div className="modal">
                    <div className="modal-content" ref={createAntiSignatureRef}>
                        <AntiSignatureForm refreshAntiSignatures={refreshAntiSignatures}
                                           requestType={RequestType.ADD_ANTISIGNATURE}
                                           setShowWindowForm={setShowCreate}
                        />
                    </div>
                </div>
                }
                <AntiSignatureTable setSignatureFeaturesFetch={setSignatureFeatures} setDescriptionFetch={setDescription}
                                    setLabelsFetch={setAntiSignatureLabels} setModelsFetch={setAntiSignatureModels}
                                    setRegionFetch={setRegion} setSignatureTypeFetch={setSignatureType} setTtlFetch={setTtl} antisignatures={antiSignatures} refreshAntiSignatures={refreshAntiSignatures}
                />
                <Snackbar
                    open={showMessage}
                    autoHideDuration={5000}
                    onClose={() => setShowMessage(false)}
                    message={responseMessage}
                    anchorOrigin={{vertical: "bottom", horizontal: "center"}}
                />
            </div>
        </div>}
    </>
}